import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

// Importaciones de entornos.
import { environment } from '../../environments/environment';

// Importaciones de interfaces.
import { IEmployee } from '../interfaces/employee.interface';

/**
 * Servicio encargado de gestionar la información del usuario autenticado en la aplicación.
 *
 * Este servicio incluye funcionalidades como la obtención de datos del perfil del usuario,
 * actualización de información personal y verificación del estado de autenticación.
 *
 * `@Injectable` permite la inyección de este servicio en otros componentes o servicios.
 * `providedIn: 'root'` garantiza que el servicio esté disponible en toda la aplicación como un singleton,
 * lo que significa que solo habrá una instancia de este servicio a lo largo de la app.
 */
@Injectable({
  providedIn: 'root',
})
export class MeService {
  /**
   * URL del endpoint para obtener la información del usuario autenticado.
   *
   * Esta propiedad almacena la URL a la que se enviarán las solicitudes HTTP para
   * acceder a los datos del usuario actual en la aplicación.
   */
  private url: string = '/me';

  /**
   * Constructor de la clase MeService.
   *
   * Inyecta el servicio `HttpClient`, que se utiliza para realizar solicitudes HTTP
   * al servidor de la API y obtener la información del usuario autenticado.
   *
   * @param http - Servicio de Angular para realizar solicitudes HTTP.
   */
  constructor(private http: HttpClient) {}

  /**
   * Obtiene los datos del usuario autenticado.
   *
   * Realiza una solicitud GET a la API para recuperar los datos del usuario actual,
   * utilizando el token de autenticación almacenado.
   *
   * @returns Una promesa que devuelve los datos del usuario.
   */
  get(): Promise<any> {
    return firstValueFrom(this.http.get(environment.apiUrl + this.url));
  }

  /**
   * Método que actualiza la información del perfil de un empleado.
   *
   * @param profile - Objeto que contiene los datos actualizados del empleado.
   *
   * @returns Una promesa que resuelve con la respuesta de la actualización.
   */
  update(profile: IEmployee): Promise<any> {
    return firstValueFrom(
      this.http.put(environment.apiUrl + this.url, {
        username: profile.username,
        email: profile.email,
        displayName: profile.displayName,
      })
    );
  }

  /**
   * Método que actualiza la contraseña del empleado actual.
   *
   * @param currentPassword - Contraseña actual del empleado.
   * @param newPassword - Nueva contraseña que se establecerá para el empleado.
   *
   * @returns Una promesa que resuelve con la respuesta de la actualización de la contraseña.
   */
  patchPassword(currentPassword: string, newPassword: string): Promise<any> {
    return firstValueFrom(
      this.http.patch(`${environment.apiUrl + this.url}/password`, {
        currentPassword,
        newPassword,
      })
    );
  }
}
